/* SpawnAnything — screen-space comic outlines (replaces the inverted-hull meshes).
   Runs inside the existing full-screen post pass: the scene is rendered into post.rt with a depth texture, and every
   pixel looks at 4 depth taps (cross, radius r px). The Laplacian of 1/z is exactly 0 on any plane (1/z is affine in
   screen space), so flat faces and distant ground never produce lines; it is large and NEGATIVE where the centre is in
   front of its neighbours (foreground side of a silhouette, convex crease) and moderately positive in concave creases.
   Scaled by z / (r · pixel footprint) the measure is distance independent:
     E > ~0.9            → line (convex crease, or the near side of a silhouette — lines sit on the object, not behind it)
     -E in [0.9, ~4]     → line (concave crease: wall meets ground, arm meets torso)
     -E > 4              → far side of a silhouette → no line (keeps the line 1-sided, so its width is exactly r)
   Crease lines fade out beyond ~260 m (depth-buffer quantisation), all lines fade into the fog like the old hulls.
   Objects that must not be outlined (particles, grass, water, rings, the building ghost) write alpha 0 into post.rt
   (see noOutline); lines are suppressed on those pixels. */
import * as THREE from 'three';

export const OUTLINE_COLOR = 0x0d0e12;

export function outlineUniforms() {
  return {
    tDepth: { value: null },
    uOL: { value: new THREE.Vector4(1, 0.3, 6000, 0.001) }, // r (px), near, far, pixel footprint per metre of depth
    uOLW: { value: new THREE.Vector2(0.05, 0.05) },          // world width (m) for full-width lines, smallest crease detail (m)
    uOLFog: { value: new THREE.Vector2(1e6, 2e6) },          // fog near, far (view depth)
    uOLFogCol: { value: new THREE.Color() },
    uOLCol: { value: new THREE.Color(OUTLINE_COLOR) },
    uOLOn: { value: 1 },
    uOLCrease: { value: 1 },                                 // 0: silhouettes only (cheaper; "Automatisch" step)
  };
}

// GLSL: float outlineAt(vec2 uv, float mark) — line coverage 0..1 at uv; mark = alpha of the scene colour (1 = outline me)
export const OUTLINE_GLSL = /* glsl */`
uniform sampler2D tDepth; uniform vec4 uOL; uniform vec2 uOLW; uniform vec2 uOLFog; uniform vec3 uOLFogCol; uniform vec3 uOLCol; uniform float uOLOn; uniform float uOLCrease;
vec3 olDbg; float olZ;
float olW(float d) { return (uOL.z - d * (uOL.z - uOL.y)) / (uOL.y * uOL.z); }
float olTap(ivec2 c, ivec2 o, ivec2 sz) { return olW(texelFetch(tDepth, clamp(c + o, ivec2(0), sz), 0).r); }
// view-space position of pixel p at view depth z (square pixels: uOL.w metres per pixel per metre of depth)
vec3 olP(ivec2 p, ivec2 sz, float z) { return vec3((vec2(p) + 0.5 - 0.5 * vec2(sz + 1)) * uOL.w * z, -z); }
// bend of the surface through pixel c along one axis (0 = straight, 0.3 ≈ 45°, 1 = 90°), from the taps either side
float olBend(ivec2 c, ivec2 o, ivec2 sz, float zc, float wa, float wb) {
  vec3 pc = olP(c, sz, zc), pa = olP(c - o, sz, 1.0 / wa), pb = olP(c + o, sz, 1.0 / wb);
  return 1.0 - dot(normalize(pc - pa), normalize(pb - pc));
}
float outlineAt(vec2 uv, float mark) {
  olZ = 1e9;
  if (uOLOn < 0.5 || mark < 0.02) return 0.0;
  ivec2 sz = textureSize(tDepth, 0) - 1;
  ivec2 c = clamp(ivec2(uv * vec2(sz + 1)), ivec2(0), sz);
  float wc = olW(texelFetch(tDepth, c, 0).r), zc = 1.0 / wc, fp = zc * uOL.w; // fp: metres per pixel at this depth
  olZ = zc;
  // line width: uOL.x px up close, thinning to 1 px once uOLW.x metres cover fewer pixels (far crowds stay readable)
  int r = int(clamp(floor(uOLW.x / fp + 0.5), 1.0, uOL.x)), rc = max(1, (r + 1) / 2);
  // silhouettes: outer taps (radius r); E = depth Laplacian of 1/z scaled to "pixels of depth jump" (0 on any plane).
  // E > 0: the centre is in front → line on the near object only, exactly r px wide
  float s = zc / (float(r) * uOL.w);
  float ol = olTap(c, ivec2(-r, 0), sz), or_ = olTap(c, ivec2(r, 0), sz), od = olTap(c, ivec2(0, -r), sz), ou = olTap(c, ivec2(0, r), sz);
  // a far side must be at least 2 px wide (taps r and r+1): single-pixel cracks in the depth buffer (T-junctions)
  // would otherwise stamp 4-dot crosses around themselves
  float sl = max(ol, olTap(c, ivec2(-r - 1, 0), sz)), sr = max(or_, olTap(c, ivec2(r + 1, 0), sz));
  float sd = max(od, olTap(c, ivec2(0, -r - 1), sz)), su = max(ou, olTap(c, ivec2(0, r + 1), sz));
  float sil = smoothstep(4.0, 6.0, max(2.0 * wc - sl - sr, 2.0 * wc - sd - su) * s);
  if (uOLCrease < 0.5) { olDbg = vec3(0.0, sil, 0.0); return sil * mark; } // uniform branch: skips the crease taps
  // creases: inner taps (radius r/2) → a centred band ~r px wide where the surface bends by more than ~35°
  // (geometric angle, so ramps and shallow slopes stay clean); skipped across depth jumps (far side of a silhouette);
  // fade out when a uOLW.y-metre detail gets smaller than a pixel and where the depth buffer runs out of precision
  float il = ol, ir = or_, id = od, iu = ou;
  if (rc != r) { il = olTap(c, ivec2(-rc, 0), sz); ir = olTap(c, ivec2(rc, 0), sz); id = olTap(c, ivec2(0, -rc), sz); iu = olTap(c, ivec2(0, rc), sz); }
  float si = zc / (float(rc) * uOL.w);
  float jx = abs(2.0 * wc - il - ir) * si, jy = abs(2.0 * wc - id - iu) * si;
  float bx = olBend(c, ivec2(rc, 0), sz, zc, il, ir), by = olBend(c, ivec2(0, rc), sz, zc, id, iu);
  float crease = max(smoothstep(0.16, 0.28, bx) * (1.0 - smoothstep(4.0, 6.0, jx)), smoothstep(0.16, 0.28, by) * (1.0 - smoothstep(4.0, 6.0, jy)));
  crease *= smoothstep(0.5, 1.2, uOLW.y / fp) * (1.0 - smoothstep(180.0, 260.0, zc));
  olDbg = vec3(max(bx, by), sil, crease);
  return max(sil, crease) * mark;
}
// line colour fogged like an opaque surface at that depth (call after outlineAt)
vec3 outlineColor() { return mix(uOLCol, uOLFogCol, smoothstep(uOLFog.x, uOLFog.y, olZ)); }
`;

/** Per-frame/per-resize uniform update. h = render-target height in pixels, r = line radius in RT pixels. */
export function updateOutline(U, camera, h, r, fog) {
  U.uOL.value.set(r, camera.near, camera.far, 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / Math.max(1, h));
  if (fog) { U.uOLFog.value.set(fog.near, fog.far); U.uOLFogCol.value.copy(fog.color); } else U.uOLFog.value.set(1e6, 2e6);
}

/** Line radius in render-target pixels for a pixel ratio: ~2 CSS px, at least 1. */
export const outlineRadius = (pixelRatio, pixelated) => pixelated ? 1 : Math.max(1, Math.round(2 * pixelRatio));

/** Exclude a material from outlines: it keeps its colour blending but writes alpha 0 (the "no outline" mark).
    Opaque ones also stop writing depth (so they cause no lines on what is behind them); give their meshes
    renderOrder >= 1 so they draw after the depth-writing scene. */
export function noOutline(mat) {
  if (!mat || mat.userData.noOutline) return mat;
  mat.userData.noOutline = true;
  const tr = mat.transparent;
  if (!tr) mat.depthWrite = false;
  mat.blending = THREE.CustomBlending;
  mat.blendEquation = THREE.AddEquation; mat.blendEquationAlpha = THREE.AddEquation;
  mat.blendSrc = tr ? THREE.SrcAlphaFactor : THREE.OneFactor; mat.blendDst = tr ? THREE.OneMinusSrcAlphaFactor : THREE.ZeroFactor;
  mat.blendSrcAlpha = THREE.ZeroFactor; mat.blendDstAlpha = THREE.ZeroFactor;
  return mat;
}
